/** How each rung of the vote landscape is coloured and described.
 *
 * Shared so the tile on the front page and the list it opens into agree.
 * They are the same fact seen twice, and a reader who clicks an amber tile
 * and lands on a blue page has been told they went somewhere else.
 *
 * The labels come from the API, not from here. The server owns where the
 * thresholds fall and what to call them; this is only the paint.
 */

export type Tone = {
  /** Solid fill, for a bar. */
  bar: string;
  /** Foreground, for the number. */
  text: string;
  /** A wash behind the heading of that band's page. */
  wash: string;
};

const TONES: Record<string, Tone> = {
  together: { bar: "bg-emerald-500", text: "text-emerald-700", wash: "from-emerald-50" },
  leaning: { bar: "bg-brand", text: "text-brand", wash: "from-brand-soft" },
  split: { bar: "bg-amber-500", text: "text-amber-700", wash: "from-amber-50" },
  opposed: { bar: "bg-gop", text: "text-gop", wash: "from-red-50" },
};

const FALLBACK: Tone = { bar: "bg-faint", text: "text-muted", wash: "from-sunk" };

/** Unknown keys get a neutral tone rather than throwing: the server may add
 *  a rung before this is redeployed, and a new band should render plainly
 *  rather than take the page down. */
export function toneFor(key: string): Tone {
  return TONES[key] ?? FALLBACK;
}

/** What each rung means, in a sentence, for the page that lists it. */
export const BAND_BLURB: Record<string, string> = {
  together:
    "These passed with both parties broadly behind them. Most laws look like this, which is not the impression you would get from the news.",
  leaning:
    "One party supported these noticeably more than the other, but neither was united against them.",
  split:
    "The two parties were far apart on these — a large minority of one side voted the other way.",
  opposed:
    "These passed over the near-total objection of one party. Almost every member of one side voted for, and almost every member of the other voted against.",
};
